import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { PermInterviewModalComponent } from 'src/app/accountmanagers/submittals/am-submittal-status-update/perm-interview-modal/perm-interview-modal.component';
import * as PermPostOfferWorkflowActions from 'src/app/root-store/perm-post-offer-workflow-store/actions/perm-post-offer-interview-modal.actions';
import { PermPostOfferService } from '../../../accountmanagers/perm-post-offer/services/perm-post-offer.service';
import { of } from 'rxjs';
import { SubmittalService } from 'src/app/recruiting/candidates/submittal/submittal-service.service';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { AmSubmittalStatusUpdateService } from 'src/app/accountmanagers/submittals/am-submittal-status-update/am-submittal-status-update.service';
import { PermPostOfferWorkflowModalManagerService } from 'src/app/accountmanagers/perm-post-offer/services/perm-post-offer-workflow-modal-manager.service';
import { PermPostOfferInterviewActionPayload } from '../models/perm-post-offer-interview-action-payload.model';
import { AMNoteItem, AMSubmittalJob } from '../../../accountmanagers/submittals/model/am-submittals.model';
import { SetPermanentJobSubmittalToInterviewResultDto } from 'src/app/accountmanagers/perm-post-offer/models/set-permanent-job-submittal-to-interview-result-dto.model';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';

@Injectable()
export class PermPostOfferInterviewModalEffects {
    static readonly unknownErrorMessage = 'An unknown error occurred.';

    openPostOfferInterviewModal$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(PermPostOfferWorkflowActions.openInterviewModal),
                map((payload: { payload: PermPostOfferInterviewActionPayload }) => {
                    return this.createPostOfferInterviewModal(payload.payload);
                })
            );
        },
        { dispatch: false }
    );

    closePostOfferInterviewModal$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(PermPostOfferWorkflowActions.closeInterviewModal),
                tap(() => {
                    this.runCancelProcesses();
                })
            );
        },
        { dispatch: false }
    );

    setPermJobSubmittalToInterview$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferWorkflowActions.setPermanentJobSubmittalToInterview),
            switchMap((actionPayload) => {
                const data = actionPayload.permPostOfferInterviewSubmittal;
                return this._permPostOfferService
                    .setPermanentJobSubmittalToInterview(data.submittalId, data.jobId, data.dto)
                    .pipe(
                        map((result: SetPermanentJobSubmittalToInterviewResultDto) => {
                            this.runPostUpdateProcesses(result.submittalJob, result.submittalNote);

                            return PermPostOfferWorkflowActions.setPermanentJobSubmittalToInterviewSuccess();
                        }),
                        catchError((error) =>
                            of(PermPostOfferWorkflowActions.setPermanentJobSubmittalToInterviewFailure(error))
                        )
                    );
            })
        );
    });

    getSubmittalStatuses$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferWorkflowActions.getPermSubmittalStatuses),
            switchMap(() =>
                this._submittalService.getSubmittalStatuses().pipe(
                    map((statuses) => PermPostOfferWorkflowActions.getPermSubmittalStatusesSuccess({ statuses })),
                    catchError((error) => of(PermPostOfferWorkflowActions.getPermSubmittalStatusesFailure({ error })))
                )
            )
        );
    });

    showToast$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferWorkflowActions.setPermanentJobSubmittalToInterviewFailure),
            map((e: unknown) => {
                return showToasterMessage({ message: PermPostOfferInterviewModalEffects.unknownErrorMessage });
            })
        );
    });

    private createPostOfferInterviewModal(actionPayload: PermPostOfferInterviewActionPayload): void {
        this._permPostOfferWorkflowModalManagerService.createModal(
            PermInterviewModalComponent,
            actionPayload,
            '540px',
            '540px'
        );
    }

    private runPostUpdateProcesses(submittalJob: AMSubmittalJob, submittalNote?: AMNoteItem): void {
        this._statusUpdateService.notifySubmittalJobUpdated(submittalJob, false, false);

        if (submittalNote) {
            this._statusUpdateService.notifyNoteUpdated(submittalNote);
        }

        this._permPostOfferWorkflowModalManagerService.closeModal();
    }

    private runCancelProcesses(): void {
        this._permPostOfferWorkflowModalManagerService.closeModal();
    }

    constructor(
        private readonly _actions$: Actions,
        private readonly _dialog: MatDialog,
        private readonly _permPostOfferService: PermPostOfferService,
        private readonly _submittalService: SubmittalService,
        private readonly _statusUpdateService: AmSubmittalStatusUpdateService,
        private readonly _permPostOfferWorkflowModalManagerService: PermPostOfferWorkflowModalManagerService,
        private readonly _featureManager: LDFeatureManager
    ) {}
}
